import React, { useContext, useEffect, useState } from 'react'
import { StyleSheet, FlatList, View, Text } from 'react-native'
import {
  BG_STYLES,
  COMMON_STYLES,
  LazyImage,
  Spinner,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'

import { FarmerProductListingScreenProps } from '../routes/types'

import ProductIcon from '../icons/ProductIcon'
import AddProductsButton from '../components/farmer-products/AddProductsBtn'
import { FarmerProducts } from '../models/farmer'
import fetchFarmerProducts from '../apis/fetchFarmerProducts'
import AddFarmerProductsModal from '../components/farmer-products/AddFarmerProductsModal'
import ToastAndroid from 'app/mocks/ToastAndroid'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'

const FarmerProductListing = ({ route }: FarmerProductListingScreenProps) => {
  const { mediaStyles } = useContext(ResponsiveContext)
  const { farmerId, isEditAllowed } = route.params
  const [loading, setLoading] = useState(true)
  const [records, setRecords] = useState<FarmerProducts[]>([])
  const [modalInfo, setModalInfo] = useState<{
    show: boolean
    record: FarmerProducts | null
  }>({
    show: false,
    record: null,
  })

  const doesUserHasEditPermission =
    typeof isEditAllowed === 'string' ? isEditAllowed === 'true' : isEditAllowed

  const fetchFarmerProductsAndUpdate = async () => {
    setLoading(true)
    const response = await fetchFarmerProducts(farmerId)
    if (response) {
      setRecords(response)
    }
    setLoading(false)
  }

  useEffect(() => {
    fetchFarmerProductsAndUpdate()
  }, [])

  if (loading) {
    return <Spinner type="on-screen" />
  }

  return (
    <>
      <FlatList
        contentContainerStyle={[
          BG_STYLES.bgWhite,
          mediaStyles({
            desktopStyle: styles['ph7/2'],
            mobileStyle: COMMON_STYLES.ph1,
          }),
        ]}
        data={records}
        keyExtractor={item => item.crop.id}
        renderItem={({ item }) => (
          <View
            style={[
              COMMON_STYLES.ph2,
              COMMON_STYLES.pv2,
              COMMON_STYLES.row,
              COMMON_STYLES.justifyCenter,
              COMMON_STYLES.flex1,
              COMMON_STYLES['mt3/2'],
              styles.container,
            ]}>
            <View style={[COMMON_STYLES.mr1, COMMON_STYLES.alignItemsCenter]}>
              <LazyImage
                imageUrl={item.crop.image_url}
                imgStyles={styles.cropImage}
              />
              <Text
                style={[
                  TYPOGRAPHY.HEADING3,
                  VARIANTS_STYLES.MEDIUM,
                  TEXT_STYLES.textNeutral100,
                  COMMON_STYLES['mt1/2'],
                ]}>
                {item.crop.name}
              </Text>
            </View>
            <View
              style={[
                COMMON_STYLES.flex1,
                COMMON_STYLES.row,
                COMMON_STYLES.flexWrap,
                COMMON_STYLES.ml2,
                styles.gap1,
              ]}>
              {item.products.map((product, index) => (
                <View key={index} style={[COMMON_STYLES['p1/2'], styles.w112]}>
                  <LazyImage
                    imageUrl={product.image_url}
                    imgStyles={styles.img96X96}
                    fallBackIcon={<ProductIcon height={96} width={96} />}
                  />

                  <View
                    style={[
                      COMMON_STYLES.fullWidth,
                      COMMON_STYLES.alignItemsCenter,
                      COMMON_STYLES.justifyCenter,
                      COMMON_STYLES['ph1/2'],

                      COMMON_STYLES['pt1/2'],
                    ]}>
                    <Text
                      style={[
                        TYPOGRAPHY.TEXT2,
                        VARIANTS_STYLES.MEDIUM,
                        TEXT_STYLES.textNeutral80,
                      ]}
                      numberOfLines={1}>
                      {product.product_name}
                    </Text>
                  </View>
                </View>
              ))}
              {doesUserHasEditPermission && (
                <AddProductsButton
                  onClick={() => setModalInfo({ show: true, record: item })}
                />
              )}
            </View>
          </View>
        )}
      />

      {modalInfo.show && (
        <AddFarmerProductsModal
          isVisible={modalInfo.show}
          farmerId={farmerId}
          crop={modalInfo.record?.crop!}
          onClose={() => setModalInfo({ show: false, record: null })}
          onSubmit={() => {
            setModalInfo({ show: false, record: null })
            fetchFarmerProductsAndUpdate().then(() =>
              ToastAndroid.show(
                'Products Added Successfully !',
                ToastAndroid.SHORT,
              ),
            )
          }}
          mappedProducts={modalInfo.record?.products || []}
        />
      )}
    </>
  )
}

const styles = StyleSheet.create({
  gap1: {
    gap: 16,
  },
  cropImage: {
    height: 100,
    width: 100,
    borderRadius: 50,
  },
  img96X96: {
    height: 96,
    width: 96,
  },
  'ph7/2': { paddingHorizontal: 56 },
  container: {
    borderRadius: 32,
    backgroundColor: '#F5F8FA',
  },
  w112: {
    width: 112,
  },
})

export default FarmerProductListing
